import { createRef } from 'react'
import { reviewActionOr, reviewChangesOr, updateElementAudit } from './audit'
import { collectionChanges } from './change-api'

import create, { UseStore } from 'zustand'
import {
  buildAutocompleteTree,
  buildReturnItems,
  findSendbackByCode
} from '../components/sendbacks/sendback-autocomplete/sendback-autocomplete-model'
import { fetchRequest, personNameWithDegree } from './'
import { getAutoCompleteInput, inputValueSelection } from './controls'
import { Audit, Provider, ReturnItem, WorksheetId } from './domain'
import { FormApi, formApi } from './form'
import { listApi } from './list'
import { WorksheetContext } from './visit-context'
import { isMultiSendbackEnabled, isReviewWorkflowState } from './workflow-api'
import { WorksheetFormApi } from './worksheet-form-api'
import { WorksheetResult, worksheetMessage } from './worksheet-service'

type ReturnPayload = ReturnItem & {
  sendbackId: string
}

export const addSendbackRequest = fetchRequest<ReturnItem, WorksheetResult<ReturnPayload>>({
  method: 'post',
  body: (args: ReturnItem) => worksheetMessage(args, 'addsendback', 'providersendback'),
})

export const addTclSendbackRequest = fetchRequest<ReturnItem[], WorksheetResult<ReturnPayload>>({
  method: 'post',
  body: (args: ReturnItem[]) => worksheetMessage(args, 'addtclsendback', 'providersendback'),
})

export type ReturnResult = WorksheetResult<ReturnPayload | ReturnPayload[]>
export const updateSendbackRequest = fetchRequest<ReturnItem, ReturnResult>({
  method: 'post',
  body: (a: ReturnItem) => {
    const args = { ...a }
    args.sendbackCodes = undefined
    return worksheetMessage(args, 'updatesendback', 'providersendback')
  },
})

export const resolveSendbackRequest = fetchRequest<ReturnItem, ReturnResult>({
  method: 'post',
  body: (a: ReturnItem) => {
    const args = { ...a }
    args.sendbackCodes = undefined
    return worksheetMessage(args, 'resolvesendback', 'providersendback')
  },
})

type DeleteArgs = WorksheetId & {
  sendbackId: string
}
export const deleteSendbackRequest = fetchRequest<DeleteArgs, ReturnResult>({
  method: 'post',
  body: (args: DeleteArgs) => worksheetMessage(args, 'deletesendback', 'providersendback'),
})

export const getSendbacksRequest = fetchRequest<DeleteArgs, ReturnResult>({
  method: 'post',
  body: (args: WorksheetId) => worksheetMessage(args, 'getsendbacks', 'providersendback'),
})

export type ReturnApi = WorksheetFormApi<ReturnItem> & {
  multiSendbackEnabled: boolean
  data: UseStore<{ 
    resolveItem: ReturnItem | null | undefined;
    editItem: ReturnItem | null | undefined;
     showCancelCodes?: boolean; sendbackProvider?: Provider }>
  resolutionForm: FormApi
  sendbackForm?: FormApi
  resolveItem(v: ReturnItem): void
  setResolved(comments: string): void
  showCancelCodes(a: ReturnItem): boolean
}

export function returnApi(context: WorksheetContext): ReturnApi {
  const { worksheet, returns, status, config, readonly } = context
  const { worksheetId, owningProvider, departmentCode, locationCode } = worksheet
  const { providers, cancelCodes } = config

  const returnReasonsList = config.returnReasonsList
  // Hierarchical reasons (with subCategory) require the multi-sendback flow regardless of location
  const multiSendbackEnabled =
    isMultiSendbackEnabled(departmentCode, locationCode) ||
    !!returnReasonsList?.some((item: ReturnItem) => item.subCategory)

  const defaultFilter = isReviewWorkflowState() ? 'all' : 'active'
  const list = listApi({
    defaultFilter,
    all: returns,
    id: (a: ReturnItem) => a.code,
    equals: (a, b) => a.code === b.code,
    active: (v) => v.active,
    setActive: (v) => (v.active = true),
    setInactive: (v) => (v.active = false),
  })

  function isListResolved(items?: ReturnItem[]) {
    let resolved = true
    const a = items || list.actions.all()
    a.forEach((v) => {
      resolved = resolved && (v.isResolved || !v.active)
    })
    return resolved
  }

  function getSendbackProvider(returns: ReturnItem[]) {
    if (!multiSendbackEnabled) {
      return null
    }
    const isResolved = isListResolved(returns)
    if (isResolved) {
      return null
    }
    const first = returns?.length ? returns[0] : null
    if (first) {
      const person = first.sendToList?.length && first.sendToList[0]
      return providers.find((v: Provider) => v.userId === person.userId)
    }
    return null
  }
  const sendbackProvider = getSendbackProvider(returns)
  const data = create(() => ({
    sendbackProvider,
    resolveItem: null,
    editItem: null,
    showCancelCodes: false,
  }))

  let editing: ReturnItem
  function editItem(v: ReturnItem) {
    editing = v
    
    if (multiSendbackEnabled) {
      /*
      const s = findSendbackByCode(v.code, returnList)
      if (s) {
      
      }*/

      v.comment = v.returnText || v.comment
      data.setState({ editItem: v });

    } else {

      api.onEdit()
      list.actions.edit(v)
     
      reason.setListValue((c: ReturnItem) => c.code === v.code)
      sendToList.value.setState({ value: v.sendToList })
    }

    comment.value.setState({ value: v.comment })
  }

  function setSendbackProvider(result: ReturnItem[]) {
    const existing = data.getState().sendbackProvider
    if (result.length && !existing) {
      const sendbackProvider = getSendbackProvider(result)
      if (sendbackProvider) {
        initialValues.sendToList = [sendbackProvider]
        data.setState({ sendbackProvider })
        sendToList.setListValue((v: Provider) => v.userId === sendbackProvider?.userId)
        sendToList.disabled.setState({ value: true })
      }
    } else {
      if (existing) {
        initialValues.sendToList = [existing]
      }
    }
    return initialValues
  }

  function addItem(v: ReturnItem) {
    async function request(update?: (v: ReturnItem) => void) {
      if (update) {
        update(v)
      }
      v.sendbackId = editing?.sendbackId

      const response = await (editing
        ? updateSendbackRequest({ worksheetId, ...v })
        : multiSendbackEnabled
        ? addTclSendbackRequest(buildReturnItems(v.sendbackCodes, v))
        : addSendbackRequest({ worksheetId, ...v }))

      if (response.success) {
        const result = response.result
        if (Array.isArray(result)) {
          result.forEach((item) => {
            list.actions.add(item)
          })
          setSendbackProvider(result)
        } else {
          const item = result
          if (update) {
            update(item)
          }

          if (editing) {
            list.actions.replace(item, (v) => v.sendbackId === editing.sendbackId)
          } else {
            list.actions.add(item)
          }

          if (!multiSendbackEnabled) {
            focusInput('')
          }
        }
        data.setState({ editItem: null })

      } else {
        setStatus((response.errors || []).join(' '))
      }
      editing = null
      list.data.setState({ editing: null })

      reason.value.setState({ value: null })
      comment.value.setState({ value: null })

      if (!multiSendbackEnabled) {
        sendToList.value.setState({ value: [] })
      }
    }

    const edit = list.data.getState().editing
    if (edit) {
      reviewChangesOr(
        {
          title: 'Review Changes For Send Back ',
          original: edit,
          update: v,
          changeReasons: {
            actualCPTCode: config.auditDownCode,
            targetCPTCode: config.auditDownCode,
          },
          fieldNames: ['reason', 'provider', 'code'],
          onComplete: (a: Audit) => {
            updateElementAudit(v, a)
            request()
          },
        },
        request,
      )
    } else {
      reviewActionOr(
        {
          title: 'Add Send Back ',
          list: context.config.auditSendBack,
          onComplete: (a) => {
            a.action = 'CREATE'
            request((v) => updateElementAudit(v, a))
          },
        },
        request,
      )
    }
  }

  function removeItem(v: ReturnItem) {
    async function request() {
      const response = await deleteSendbackRequest({
        worksheetId: worksheet.worksheetId,
        sendbackId: v.sendbackId,
      })
      if (response.success) {
        list.actions.remove(v)
      } else {
        setStatus((response.errors || []).join(' '))
      }
    }

    reviewActionOr(
      {
        title: 'Remove Send Back  ',
        list: context.config.auditTracking,
        onComplete: (a) => {
          a.action = 'DELETE'
          v.audit = a
          request()
        },
      },
      request,
    )
  }

  function resolveItem(v: ReturnItem) {
    v.resolutionInformation = { ...v.resolutionInformation }
    data.setState({ showCancelCodes: showCancelCodes(v), resolveItem: v })
  }

  function setResolved(comments: string, cancelCode?: string | number) {
    const item: ReturnItem = data.getState().resolveItem
    item.resolutionInformation ||= {}
    item.resolutionInformation.resolutionComment = comments
    item.cancelCode = cancelCode ? cancelCode + '' : undefined

    async function request() {
      const response = await resolveSendbackRequest(item)
      if (response.success) {
        const result = response.result as ReturnItem
        if (result.sendbackId) {
          item.isResolved = true
          list.actions.replace(item, (v) => v.sendbackId === item.sendbackId)

          const isResolved = isListResolved()
          if (isResolved) {
            data.setState({ sendbackProvider: null })
            sendToList.disabled.setState({ value: false })
            sendToList.value.setState({ value: [] })
          }
        }
      } else {
        setStatus((response.errors || []).join(' '))
      }
    }

    data.setState({ resolveItem: null })
    list.actions.refresh()
    request()
  }
  const initialValues: any = {
    reason: null,
    sendToList: sendbackProvider ? [sendbackProvider] : [],
    comment: null,
  }

  const setStatus = status.getState().setStatus
  const returnList = (multiSendbackEnabled ? buildAutocompleteTree(returnReasonsList) : returnReasonsList) || []
  const listLabel = multiSendbackEnabled ? (v) => v.category : (v) => v.description

  const form: FormApi<any> = formApi({
    disabled: readonly,
    values: initialValues,
    fields: {
      reason: {
        label: 'Reason',
        list: returnList,
        listLabel,
        required: true,
        inputRef: createRef(),
        onEnterValid: () => {
          form.submit()
        },
      },
      sendToList: {
        style: {
          minHeight: 24,
          maxHeight: 24,
          overflowY: 'visible',
        },
        disabled: !!sendbackProvider,
        label: 'Send To',
        list: providers,
        listLabel: (v: Provider) => personNameWithDegree(v),
        onEnter: () => {
          form.submit()
        },
      },
      comment: {
        label: 'Comments',
        multiline: true,
        onEnter: () => form.submit(),
      },
    },
    valid: (v) => {
      if (multiSendbackEnabled) {
        return v.reason && v.sendToList?.userId
      }
      return v.reason && !!v.sendToList && !!v.sendToList.length
    },
    onSubmit: async (v) => {
      const r = v.reason
      const sendToList = multiSendbackEnabled ? [v.sendToList] : v.sendToList

      setStatus('')

      if (multiSendbackEnabled && v?.reason?.length) {
        let item = {
          ...editing,
          ...{
            worksheetId,
            code: r[0].code || editing?.code,
            returnText: r[0].description || editing?.returnText,
            sendToList,
            comment: v.comment ? v.comment.trim() : undefined,
            sendbackCodes: r as any,
          },
        }

        if (editing) {
          const items = buildReturnItems(r, item)
          if (items?.length) {
            item = { ...item, ...items[0] }
          }
        }
         else  {
        const items = buildReturnItems(r, item)
        if (!items?.length) {
          return
        }
      }
     
        addItem(item)
      } else {
        addItem({
          sendToList,
          code: r.code,
          returnText: r.description,
          comment: v.comment ? v.comment.trim() : undefined,
        })
      }
    },
  })

  const { reason, sendToList, comment } = form.controls

  const inputElement = getAutoCompleteInput(reason.inputRef)
  const focusInput = inputValueSelection(inputElement)

  const listChanges = collectionChanges(list, {
    changeLabel: (v: ReturnItem[]) => v.map((u) => u.reason.returnText).join(', '),
  })

  function setPhysician(_: String, login: string) {
    if (!multiSendbackEnabled) {
      const defaultProvider = providers.find((v: Provider) => v.login === login)
      initialValues.sendToList = defaultProvider ? [defaultProvider] : []
      sendToList.value.setState({ value: initialValues.sendToList })
    }
  }

  if (multiSendbackEnabled) {
    sendToList.setListValue((v: Provider) => v.userId === sendbackProvider?.userId)
    initialValues.sendToList = sendbackProvider ? [sendbackProvider] : []
  } else {
    setPhysician(null, owningProvider?.login)
  }

  function showCancelCodes(a: ReturnItem) {
    if (!cancelCodes?.length) {
      return false
    }
    let show = true
    list.actions
      .all()
      .filter((v) => v.sendbackId !== a.sendbackId)
      .forEach((v) => {
        show = show && (v.isResolved || !v.active)
      })
    return show
  }
  const resolutionForm = formApi<{ comments: any; cancelCode?: { code: string | number; description: string } }>({
    values: { comments: null, cancelCode: null },
    fields: {
      cancelCode: {
        label: 'Cancel Code',
        list: cancelCodes || [],
        listLabel: (v) => v.description,
        required: true,
        onEnter: () => {
          form.submit()
        },
      },
      comments: {
        label: 'Comments',
        style: { width: '100%' },
        multiline: true,
        rows: 2,
      },
    },
    onSubmit: (v) => {
      const cancelCode = v.cancelCode?.code

      if (data.getState().showCancelCodes) {
        if (cancelCode === undefined) {
          return
        }
      }

      api.setResolved(v?.comments, cancelCode)
    },
    resetValues: () => ({ comments: null }),
  })

  const sendbackForm = multiSendbackEnabled ? formApi<{ comments: any; cancelCode?: { code: string | number; description: string } }>({
    values: { comments: null },
    fields: {
      comments: {
        label: 'Comments',
        style: { width: '100%' },
        required: true,
        multiline: true,
        rows: 11,
      },
    },
    onSubmit: (v) => {
      if(v.comments?.length){
        addItem({ ...editing, returnText: v.comments, comment: v.comments })
      }
    },
    resetValues: () => ({ comments: null }),
  }) : undefined;

  const api = {
    form,
    list,
    listChanges,
    context,
    multiSendbackEnabled,
    data: data,
    resolutionForm,
    sendbackForm,
    addItem,
    editItem,
    removeItem,
    setPhysician,
    onEdit: () => null,
    resolveItem,
    setResolved,
    showCancelCodes,
  }

  // @ts-ignore cast
  return api
}
